// Operator › Digital employees (`/operator/des?p=`; coverage audit row 84; Reports / Ops UX pass
// 2026-10-21): every DE across the granted companies with its weekly scorecard summary, LiteLLM
// spend flags and (M5-05) its supervisor, week cost, supervision time and waiting approvals
// (`GET /v1/operator/digital-employees?week=`, one call). The 56 px location header, a 40 px
// toolbar (week, search), four compact tiles and the table, Open DE and Open company on hover.
// A company without `kpi` shows "KPI off" instead of a band (its scorecard is null on the server).
import {
  Badge,
  BandBadge,
  EmptyState,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  SearchField,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  BotIcon,
  HourglassIcon,
  TimerIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { MeResponse } from "../../api/types";
import { recentWeeks } from "../../focus/focus";
import { weekRangeLabel } from "../../focus/weeks";
import { hMm } from "../agency/agency-queries";
import { formatMoney, formatScore } from "../format";
import { operatorDesQuery } from "../ops-queries";
import {
  OpsBody,
  OpsFrame,
  operatorCrumb,
  RowActions,
  RowLinkAction,
  RowSub,
  TABLE_ROW_HOVER,
  Tiles,
} from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import { featureState } from "../use-feature";

export function OperatorDes({
  api,
  me,
  week,
  onWeek,
}: {
  api: Api;
  me: MeResponse;
  week: string;
  onWeek: (week: string) => void;
}) {
  const des = useQuery(operatorDesQuery(api, week));
  const [search, setSearch] = useState("");
  const weeks = useMemo(() => {
    const list = recentWeeks(new Date(), 12);
    return list.includes(week) ? list : [week, ...list];
  }, [week]);
  const rows = des.data?.data ?? [];
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? rows.filter(
        (r) =>
          r.digital_employee.display_name.toLowerCase().includes(needle) ||
          r.company.name.toLowerCase().includes(needle),
      )
    : rows;
  const active = rows.filter((r) => r.digital_employee.status === "active").length;
  const pending = rows.reduce((a, r) => a + r.pending_approvals, 0);
  const unsupervised = rows.filter((r) => !r.supervisor?.name).length;
  const minutes = rows.reduce((a, r) => a + r.supervision_minutes, 0);
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Digital employees" }]}
        icon={BotIcon}
        title="Digital employees"
        count={des.isPending ? undefined : rows.length}
      />
      <Toolbar bleed>
        <NativeSelect
          size="sm"
          aria-label="Week"
          value={week}
          onChange={(e) => onWeek(e.target.value)}
          className="w-auto"
        >
          {weeks.map((w) => (
            <NativeSelectOption key={w} value={w}>
              {w} · {weekRangeLabel(w)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <SearchField
          placeholder="Search DEs and companies…"
          aria-label="Search digital employees"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Toolbar>
      <OpsBody>
        <Tiles data-slot="des-tiles" aria-label="Digital employees this week">
          <StatTile
            size="sm"
            label="Active"
            icon={BotIcon}
            value={des.isPending ? "…" : active}
            detail={`of ${rows.length} digital employees`}
          />
          <StatTile
            size="sm"
            label="Waiting approvals"
            icon={HourglassIcon}
            value={des.isPending ? "…" : pending}
            detail="now"
          />
          <StatTile
            size="sm"
            label="Unsupervised"
            icon={TriangleAlertIcon}
            value={des.isPending ? "…" : unsupervised}
            detail={unsupervised ? "no active supervisor" : "every DE has a supervisor"}
          />
          <StatTile
            size="sm"
            label="Supervision"
            icon={TimerIcon}
            value={des.isPending ? "…" : hMm(minutes)}
            detail={`h:mm, week ${week}`}
          />
        </Tiles>
        {des.isPending ? (
          <Loading label="Loading digital employees" />
        ) : des.isError ? (
          <LoadError what="Digital employees" error={des.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={BotIcon}
            title="No digital employees yet"
            description="DEs set up in your companies appear here."
          />
        ) : (
          <Table data-slot="operator-des">
            <TableCaption>
              One row per DE; spend flags from LiteLLM; week cost from the seat's ledger;
              supervision in h:mm.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Digital employee</TableHead>
                <TableHead scope="col">Status</TableHead>
                <TableHead scope="col">Band</TableHead>
                <TableHead scope="col" className="text-right">
                  KPI score
                </TableHead>
                <TableHead scope="col">Spend</TableHead>
                <TableHead scope="col">Supervisor</TableHead>
                <TableHead scope="col" className="text-right">
                  Week cost
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Supervision
                </TableHead>
                <TableHead scope="col" className="text-right" title="Pending approvals">
                  Approvals
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((r) => {
                const kpi = featureState(me, r.company.id, "kpi").enabled;
                const de = r.digital_employee;
                return (
                  <TableRow
                    key={de.membership_id}
                    data-de={de.membership_id}
                    className={TABLE_ROW_HOVER}
                  >
                    <TableHead scope="row" className="max-w-56 font-normal whitespace-normal">
                      <WorkerBadge kind="de" size="sm" className="mr-1 align-middle" />
                      <Link
                        to="/c/$slug/agents/de/$id"
                        params={{ slug: r.company.slug, id: de.membership_id }}
                        search={{ p: week }}
                        className="font-medium underline-offset-4 hover:underline"
                      >
                        {workerName(de.display_name)}
                      </Link>
                      <RowSub>
                        {r.company.name} · {de.seat?.title ?? "No seat"}
                      </RowSub>
                    </TableHead>
                    <TableCell>
                      <Badge variant={de.status === "active" ? "success" : "neutral"}>
                        {de.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {!kpi ? (
                        <Badge variant="outline">KPI off</Badge>
                      ) : r.scorecard ? (
                        <BandBadge band={r.scorecard.band} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {kpi ? formatScore(r.scorecard?.kpi_score ?? null) : "—"}
                    </TableCell>
                    <TableCell>
                      {r.spend_attention ? (
                        <Badge variant="warning" title={r.spend_attention}>
                          Needs attention
                        </Badge>
                      ) : r.spend_alert ? (
                        <Badge variant="warning">≥ 80% of budget</Badge>
                      ) : (
                        <span className="text-muted-foreground">OK</span>
                      )}
                    </TableCell>
                    <TableCell data-slot="de-supervisor">
                      {r.supervisor?.name ? (
                        <div className="flex flex-col">
                          <span>{r.supervisor.name}</span>
                          <RowSub>{r.supervisor.title}</RowSub>
                        </div>
                      ) : (
                        <Badge
                          variant="warning"
                          title={
                            r.supervisor
                              ? `${r.supervisor.title} has no active holder`
                              : "The DE's seat has no supervisor seat"
                          }
                        >
                          Unsupervised
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(r.cost_minor, r.currency)}
                    </TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      title="Approved supervision (h:mm)"
                    >
                      {hMm(r.supervision_minutes)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.pending_approvals > 0 ? (
                        <Badge variant="info">{r.pending_approvals}</Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions>
                        <RowLinkAction
                          icon={BotIcon}
                          label={`Open ${workerName(de.display_name)}`}
                          render={
                            <Link
                              to="/c/$slug/agents/de/$id"
                              params={{ slug: r.company.slug, id: de.membership_id }}
                              search={{ p: week }}
                            />
                          }
                        />
                        <RowLinkAction
                          icon={ArrowUpRightIcon}
                          label={`Open ${r.company.name}`}
                          render={
                            <Link
                              to="/c/$slug"
                              params={{ slug: r.company.slug }}
                              search={{ p: week }}
                            />
                          }
                        />
                      </RowActions>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </OpsBody>
    </OpsFrame>
  );
}
